/**
 * Reset Conversation
 *
 * Ends the conversation the widget is holding: logs out of the chat provider and
 * drops the local user, messages and composition.
 *
 * This is the teardown behind `Gladly.clearUser`, which reaches it by two paths —
 * `resetConversation` when it holds an action environment, `resetConversationLocally`
 * when it holds only a store. Leaving out `chatSessionEnded()` leaves the Beacon
 * heartbeat pinging a conversation that no longer exists, which is why the three
 * steps live together rather than at each call site.
 *
 * `bootstrap.ts` imports this module statically, so its imports must stay on the
 * always-loaded tier that `make bundle-budgets` gates.
 */

import { clearAgentActivity } from '@/actions/messages/agentActivity';
import { clearInitialSendContext } from '@/actions/messages/initialSendContext';
import type { ActionEnvironment } from '@/domain/actionEnvironment';
import { hasUserCredentials } from '@/domain/user';
import { beaconConversationEnded } from '@/lib/beacon/beaconConnection';
import { chatSessionEnded } from '@/lib/beacon/beaconHeartbeat';
import { logger } from '@/lib/logger';
import type { WidgetStore } from '@/stores/storeTypes';

/**
 * The store surface the local teardown touches. A slice of `WidgetStore` rather
 * than of `StoreEnvironment` so both satisfy it: actions hold the latter, and
 * `bootstrap.ts` holds a `WidgetStoreApi` it reads through `getState()`.
 */
type ConversationStore = Pick<
  WidgetStore,
  'app' | 'appMutations' | 'messagesMutations' | 'testModeMutations' | 'userMutations'
>;

export async function resetConversation(env: ActionEnvironment): Promise<void> {
  env.logAction('session/resetConversation');

  try {
    // Read live rather than from a snapshot: a caller may have awaited a widget
    // load to reach this gateway at all.
    if (hasUserCredentials(env.store.user)) {
      await env.gateways.chatGateway.logout();
    }
  } catch (error) {
    logger.error('[resetConversation] error during logout', error);
  } finally {
    // In `finally`, not after the await: a provider that failed to log out still
    // leaves this side holding a conversation.
    resetConversationLocally(env.store);
  }
}

/**
 * The reset without the provider logout, for a caller that has a store but no
 * gateway. Named for what it omits because omitting it is silent — the widget
 * clears either way, and a skipped logout only surfaces later as a provider
 * session that never ended. Prefer `resetConversation` where an environment is
 * in hand.
 */
export function resetConversationLocally(store: ConversationStore): void {
  // chat-rocket's logout unsubscribes before disconnecting, so
  // chatClientDisconnected never fires and nothing else stops the heartbeat.
  chatSessionEnded();
  beaconConversationEnded();
  clearAgentActivity(store);
  store.messagesMutations.clearMessages();
  clearInitialSendContext();
  store.userMutations.userDeleted();
  // Ends any test session here rather than in the gateway's logout: this runs on
  // both clearUser paths, including the credential-less one where logout is
  // skipped — the common state after a `navigateToUrl` reload. A no-op in
  // production, where no test session id is ever set.
  store.testModeMutations.testSessionEnded();
}
